/**
 * route-descriptor — the schema-free ROUTE-projection of a Domain Model (#705 S2).
 *
 * ## Why this exists
 *
 * The studio's eager entry only needs, per generated cluster, the ROUTE TABLE +
 * the plugin's (lazy) editor map — NOT the full 16-kind graph. `resolveRoutes`
 * (`sdk/src/router/route-resolution.ts`) reads only each route entity's
 * `meta.path`/`meta.title` and its `routes-to` target, then resolves the editor
 * via `plugin.editors[id]`; it NEVER looks up the component/store/service/schema
 * entity in the genome. So the route table is a PURE projection of a cluster's
 * light identity — `{ id, name, version, nodes:[{ name, plural }] }` — with NO
 * schema literals, NO capabilities, NO `generate()` machinery.
 *
 * A {@link RouteDescriptor} is exactly that light identity. {@link
 * projectRouteEntities} produces the SAME route `EntityDef`s `generate(model)`
 * emits for a cluster (the `route/home` index + one `route/{node}` per node),
 * proven byte-identical by the `route-descriptor` gate. This lets the studio
 * compose a cluster's routes onto the eager entry from a schema-free descriptor
 * (#705 S3) while the full graph — schemas + the generator + the strand kernel —
 * moves to the lazy full-graph phase (`genome-full.ts`).
 *
 * ## No drift by construction
 *
 * A descriptor could disagree with its model. {@link deriveRouteDescriptor}
 * derives the descriptor FROM a model (the single source of truth); a Node-side
 * gate then asserts an authored descriptor deep-equals the derived one, so the
 * two can never silently diverge (the descriptor stays schema-free; the drift
 * check runs off the entry, at test time).
 *
 * PURE: no I/O, no React. Same descriptor ⇒ byte-identical route `EntityDef[]`.
 *
 * @module @ubiclouds/v5-genome-gen/route-descriptor
 */

import type { EntityDef } from '@ubiclouds/v5-sdk/genome';
import type { DomainModel } from './domain-model.js';

/** One node's route identity — its stem + its REST/list plural. Schema-free. */
export interface RouteDescriptorNode {
  /** The node's stable lower-kebab stem (drives `route/{name}` + `component/{name}-view`). */
  readonly name: string;
  /** The node's human plural (the list route path `/{plural}` + the route title). */
  readonly plural: string;
}

/**
 * The schema-free ROUTE identity of a generated cluster — everything the router
 * needs to project the cluster's route table WITHOUT the full graph. Mirrors the
 * light slice of a {@link DomainModel} that determines its route entities.
 */
export interface RouteDescriptor {
  /** The cluster namespace (`model.id`). */
  readonly id: string;
  /** The cluster's human name (`model.name`) — the `route/home` index title. */
  readonly name: string;
  /** The cluster version (`model.version`) — carried as the plugin's `version`. */
  readonly version: string;
  /** The cluster's viewable nodes, in authoring order. */
  readonly nodes: readonly RouteDescriptorNode[];
}

/**
 * Derive the schema-free {@link RouteDescriptor} from a full {@link DomainModel}
 * — the single source of truth. The no-drift gate asserts an authored descriptor
 * deep-equals `deriveRouteDescriptor(model)`, so a hand-authored descriptor can
 * never diverge from its model.
 */
export function deriveRouteDescriptor(model: DomainModel): RouteDescriptor {
  return {
    id: model.id,
    name: model.name,
    version: model.version,
    nodes: model.nodes.map((n) => ({ name: n.name, plural: n.plural })),
  };
}

/**
 * Project the RAW route `EntityDef`s of a cluster from its descriptor — byte-
 * identical to the `route` entities `generate(model)` emits for the same cluster
 * (the `route-descriptor` gate locks this). Emits:
 *
 *   - the app index route `{id}:route/home` → `component/index` (path `/`, title
 *     `name`) — every viewable cluster has one (`compileGenome`'s `emitIndex`);
 *   - one `{id}:route/{node.name}` → `component/{node.name}-view` per node
 *     (path `/{node.plural}`, title `node.plural`).
 *
 * RAW: no studio route-placement rewrite (the `routePrefix`/`omitIndex`/
 * `appHomePath` re-home lives in the app-tier `applyRoutePlacement`, applied by
 * BOTH the full and the route-only studio plugins). The returned list is sorted
 * by `meta.id` to match `generate()`'s sorted entity map.
 */
export function projectRouteEntities(descriptor: RouteDescriptor): EntityDef[] {
  const ns = descriptor.id;
  const entities: EntityDef[] = [
    {
      kind: 'route',
      meta: { id: `${ns}:route/home`, path: '/', title: descriptor.name },
      refs: [{ rel: 'routes-to', target: `${ns}:component/index` }],
    },
    ...descriptor.nodes.map<EntityDef>((node) => ({
      kind: 'route',
      meta: { id: `${ns}:route/${node.name}`, path: `/${node.plural}`, title: node.plural },
      refs: [{ rel: 'routes-to', target: `${ns}:component/${node.name}-view` }],
    })),
  ];
  return entities.sort((a, b) => routeId(a).localeCompare(routeId(b)));
}

/** The `meta.id` of a route entity (a stable sort key). */
function routeId(entity: EntityDef): string {
  const id = entity.meta?.['id'];
  return typeof id === 'string' ? id : '';
}
